var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import login from 'root/src/client/logic/auth/thunks/login';
import signUp from 'root/src/client/logic/auth/thunks/signUp';
import resetPassword from 'root/src/client/logic/auth/thunks/resetPassword';
import transferAccount from 'root/src/client/logic/auth/thunks/transferAccount';
import { signInWithEmail } from 'root/src/client/v2/auth/thunks/signInWithEmail';
import { authApi } from 'root/src/shared/util/authApi';
import getDomainRoute from 'root/src/client/util/getDomainRoute';
import { RESET_PASSWORD_ROUTE_ID } from 'root/src/shared/descriptions/routes/routeIds';
import { userGroups } from 'root/src/shared/constants/auth';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { Provider } from 'root/src/shared/@types/providers';
import { useSignInWithProvider } from 'root/src/client/web/auth/hooks/useSignInWithProvider';
import { PASSWORD_SCHEMA } from 'root/src/client/v2/auth/schemas/password.schema';
import { RedesignSignInWithProviderButton } from 'root/src/client/v2/auth/redesign/RedesignSignInWithProviderButton';
import { GoogleIcon, TwitchIcon, TikTokIcon } from 'root/src/client/v2/common/components/icons/providerIcons';
import { DiscordIcon } from 'root/src/client/v2/common/components/icons/DiscordIcon';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Checkbox } from 'root/src/client/v2/common/components/ui/checkbox';
import { FieldError } from 'root/src/client/v2/common/components/ui/field';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { AUTH_FORM_COPY } from 'root/src/client/v2/auth/features/AuthForm/authFormCopy';
const SOCIAL_PROVIDERS = [Provider.Google, Provider.Twitch, Provider.TikTok, Provider.Discord];
const PROVIDER_ICONS = {
    [Provider.Google]: GoogleIcon,
    [Provider.Twitch]: TwitchIcon,
    [Provider.TikTok]: TikTokIcon,
    [Provider.Discord]: DiscordIcon,
};
const ACCOUNT_TYPES = [
    { value: userGroups.viewer, label: 'Viewer' },
    { value: userGroups.creator, label: 'Creator' },
    { value: userGroups.brand, label: 'Brand' },
];
const inputClassName = 'w-full rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 '
    + 'placeholder:text-muted-foreground/30 transition-colors focus:border-primary/30 focus:outline-none';
// The dialog packs the form into a 480px panel; the standalone pages have a full viewport, so the
// same markup is rendered one step larger. Only the type scale and control heights differ.
const SIZE_STYLES = {
    compact: {
        title: 'text-lg',
        subtitle: 'text-xs',
        control: 'h-9 text-sm',
        body: 'mt-5 space-y-4',
    },
    page: {
        title: 'text-2xl',
        subtitle: 'text-sm',
        control: 'h-11 text-sm',
        body: 'mt-6 space-y-5',
    },
};
// Compact icon-only provider button, signing in through an OIDC popup so the caller stays put.
const ProviderIconButton = ({ provider, size, onSignedIn }) => {
    // Sign in via OIDC popup. On success the popup dispatches `cookiechange` (see useSignInWithProvider),
    // so `useAuthentication` picks up the new session reactively (navbar + queries refresh).
    const { onSignIn, providerLabel, isLoading } = useSignInWithProvider({
        provider,
        popup: true,
        popupOptions: {
            onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
                onSignedIn === null || onSignedIn === void 0 ? void 0 : onSignedIn();
            }),
            onError: (error) => {
                toast.error('Could not sign in', { description: error.message });
            },
        },
    });
    const Icon = PROVIDER_ICONS[provider];
    return (<button type="button" onClick={onSignIn} disabled={isLoading} aria-label={`Sign in with ${providerLabel}`} className={cn('flex flex-1 items-center justify-center rounded-lg border border-solid border-white/[0.06] bg-white/[0.04]', 'transition-all hover:border-white/[0.12] hover:bg-white/[0.08] disabled:opacity-60', SIZE_STYLES[size].control)}>
			{isLoading
            ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-t-transparent"/>
            : <Icon className="h-4 w-4"/>}
		</button>);
};
// The redesign email/password + social auth form, shared by the SignInDialog and the standalone
// /sign-in and /sign-up pages. Owns the three panels (sign in, sign up, forgot password) and the
// submit wiring; the caller owns the surrounding shell and decides what a mode switch means (swap
// the panel in place, or navigate to the other page).
export const RedesignAuthForm = ({ mode, onModeChange, size = 'compact', providerLayout = 'compact', onSignIn, onSignInSuccess, onProviderSignIn, newsletterOptIn = false, footnote, className, }) => {
    const dispatch = useAppDispatch();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [forgotSent, setForgotSent] = useState(false);
    const [accountType, setAccountType] = useState(userGroups.creator);
    const [newsletterSub, setNewsletterSub] = useState(false);
    const { loading, success, error, run } = useAsyncAction();
    const isSignup = mode === 'signup';
    const isForgot = mode === 'forgot';
    // The two "set a new password" panels: password plus confirmation only, no email and no providers,
    // because the visitor is already identified by the code / token in the URL.
    const isPasswordSet = mode === 'reset' || mode === 'transfer';
    const needsConfirmPassword = isSignup || isPasswordSet;
    const copy = AUTH_FORM_COPY[mode];
    const styles = SIZE_STYLES[size];
    const PasswordVisibilityIcon = showPassword ? VisibilityOffIcon : VisibilityIcon;
    // Clear the transient panel state whenever the caller switches panels. Email and password are
    // deliberately kept, so someone who typed an email and then switched panels does not retype it.
    useEffect(() => {
        setErrorText('');
        setConfirmPassword('');
        setForgotSent(false);
    }, [mode]);
    const handleSubmit = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        event.preventDefault();
        setErrorText('');
        if (needsConfirmPassword) {
            // Same bar the legacy signup and reset forms enforced (signUpSchema / resetPasswordSchema):
            // password strength plus a retyped confirmation, so a typo cannot silently become the password.
            const passwordCheck = PASSWORD_SCHEMA.safeParse(password);
            if (!passwordCheck.success) {
                setErrorText((_b = (_a = passwordCheck.error.issues[0]) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'Please choose a stronger password.');
                return;
            }
            if (password !== confirmPassword) {
                setErrorText('Passwords do not match.');
                return;
            }
        }
        try {
            yield run(() => __awaiter(void 0, void 0, void 0, function* () {
                if (isForgot) {
                    yield authApi.forgotPassword({ email, redirectUrl: getDomainRoute(RESET_PASSWORD_ROUTE_ID) });
                    setForgotSent(true);
                    toast.success('Reset link sent', { description: `Check ${email} for the link.` });
                }
                else if (isPasswordSet) {
                    // Both legacy thunks own their own navigation: resetPassword reads `?code=` and pushes to
                    // sign-in, transferAccount reads `?token=`, signs the user in and pushes to their default
                    // route. Nothing to do here beyond dispatching the right one.
                    yield dispatch(mode === 'reset' ? resetPassword({ password }) : transferAccount({ password }));
                }
                else if (isSignup) {
                    // signUp pushes to /verify-account itself, so there is no post-signup navigation here.
                    yield dispatch(signUp({
                        email,
                        password,
                        userGroup: accountType,
                        redirectRoute: undefined,
                        newsletterSub: newsletterOptIn && accountType === userGroups.brand ? newsletterSub : false,
                    }));
                }
                else {
                    // With a callback the caller owns what happens next (the dialog closes in place). Without
                    // one, `login` is called with no `onSignIn` so it runs its own post-login navigation
                    // (afterLoginRouteSelector -> group default route), which is what a standalone sign-in
                    // page needs.
                    yield dispatch(onSignIn
                        ? signInWithEmail({ email, password, onSignIn })
                        : login({ email, password, onSignIn: undefined }));
                    toast.success('Signed in', { description: 'Welcome back!' });
                }
            }), { onSuccess: mode === 'signin' ? onSignInSuccess : undefined });
        }
        catch (e) {
            const messages = e && typeof e === 'object' ? Object.values(e) : [];
            const message = messages[0] || copy.errorFallback;
            setErrorText(message);
            toast.error(copy.errorTitle, { description: message });
        }
    });
    return (<div className={className}>
			<AnimatePresence mode="wait">
				<motion.div key={mode} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
					<h2 className={cn('font-display font-semibold', styles.title)}>{copy.title}</h2>
					<p className={cn('mt-1 text-muted-foreground/60', styles.subtitle)}>{copy.subtitle}</p>

					<div className={styles.body}>
						{isForgot && forgotSent ? (<div className="rounded-lg border border-solid border-neon-green/20 bg-neon-green/10 p-4 text-xs leading-relaxed text-neon-green">
								Check your inbox. We sent a password reset link to <span className="font-medium">{email}</span>.
							</div>) : (<>
								{!isForgot && !isPasswordSet && (<>
										{providerLayout === 'compact' ? (<div className="flex gap-2">
												{SOCIAL_PROVIDERS.map(provider => (<ProviderIconButton key={provider} provider={provider} size={size} onSignedIn={onProviderSignIn}/>))}
											</div>) : (
                // Full-page redirect flow (no popup): the OIDC callback returns to this page and the
                // route's `unauthenticated` guard forwards the now signed-in visitor onwards, which is
                // how the legacy sign-in page's provider buttons behaved.
                <div className="flex flex-col gap-2.5">
												{SOCIAL_PROVIDERS.map(provider => (<RedesignSignInWithProviderButton key={provider} provider={provider} className={styles.control}/>))}
											</div>)}

										<div className="relative">
											<div className="absolute inset-0 flex items-center">
												<div className="w-full border-t border-white/[0.06]"/>
											</div>
											<div className="relative flex justify-center text-[10px]">
												<span className="bg-card px-2 text-muted-foreground/40">email</span>
											</div>
										</div>
									</>)}

								<form onSubmit={handleSubmit} className="space-y-2.5">
									{!isPasswordSet && (<input type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className={cn(inputClassName, styles.control)}/>)}
									{!isForgot && (<div className="relative">
											<input type={showPassword ? 'text' : 'password'} required placeholder={isPasswordSet ? 'New password' : 'Password'} value={password} onChange={e => setPassword(e.target.value)} className={cn(inputClassName, styles.control, 'pr-9')}/>
											<button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/25 transition-colors hover:text-muted-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
												<PasswordVisibilityIcon sx={{ fontSize: 14 }}/>
											</button>
										</div>)}

									{needsConfirmPassword && (<input type={showPassword ? 'text' : 'password'} required placeholder="Confirm password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={cn(inputClassName, styles.control)}/>)}

									{isSignup && (<div>
											<span className="mb-1.5 block text-[10px] uppercase tracking-wider text-muted-foreground/40">I am a</span>
											<div className="flex gap-1 rounded-lg border border-solid border-white/[0.06] bg-white/[0.03] p-1">
												{ACCOUNT_TYPES.map((type) => {
                    const active = accountType === type.value;
                    return (<button key={type.value} type="button" onClick={() => setAccountType(type.value)} className={cn('relative flex-1 rounded-lg py-2 text-xs font-medium transition-colors', active ? 'text-neon-green' : 'text-muted-foreground hover:text-foreground')}>
															{active && (<motion.span layoutId="signup-account-type" className="absolute inset-0 rounded-lg border border-solid border-neon-green/50 bg-neon-green/10" transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}/>)}
															<span className="relative z-10">{type.label}</span>
														</button>);
                })}
											</div>
										</div>)}

									{isSignup && newsletterOptIn && accountType === userGroups.brand && (<label htmlFor="auth-newsletter-sub" className="flex cursor-pointer items-center gap-2 pt-0.5 text-xs text-muted-foreground">
											<Checkbox id="auth-newsletter-sub" checked={newsletterSub} onCheckedChange={setNewsletterSub}/>
											Subscribe to our newsletter
										</label>)}

									{mode === 'signin' && (<div className="flex justify-end">
											<button type="button" onClick={() => { setErrorText(''); onModeChange('forgot'); }} className="text-[11px] text-primary/60 transition-colors hover:text-primary">
												Forgot password?
											</button>
										</div>)}

									<FieldError>{errorText}</FieldError>

									<Button type="submit" variant="brand" loading={loading} success={success} error={error} className={cn('w-full rounded-lg font-medium', styles.control)}>
										{copy.submitLabel}
									</Button>

									{footnote}
								</form>
							</>)}
					</div>
				</motion.div>
			</AnimatePresence>
		</div>);
};
// "New to Dare Drop? Sign up free" footer. Split out of the form so the dialog can pin it to the
// bottom of its panel while the pages place it under the card, both off the same copy.
export const AuthFormModeSwitch = ({ mode, onModeChange, className }) => {
    const { switchPrompt, switchLabel, switchTo } = AUTH_FORM_COPY[mode];
    // Panels the user must finish (transfer-account) declare no switch target, so they get no way out.
    if (!switchTo) {
        return null;
    }
    return (<p className={cn('text-center text-xs text-muted-foreground/40', className)}>
			{switchPrompt}{' '}
			<button type="button" onClick={() => onModeChange(switchTo)} className="font-medium text-primary/70 transition-colors hover:text-primary">
				{switchLabel}
			</button>
		</p>);
};
